// agentEmojiIcon — mapeia o emoji gravado de um agente/registro para um ícone
// do Huge Icons. O DADO (a.emoji) NÃO muda no banco; só o RENDER vira ícone de
// traço, consistente com o resto da UI (pedido do Pedro: "troca esses emojis").
//
// Qualquer emoji não mapeado (ou ausente) cai no fallback de robô — nenhum
// agente renderiza emoji cru. Uso:
//   import { HugeiconsIcon } from '@hugeicons/react';
//   import { iconForAgentEmoji } from '../../lib/agentEmojiIcon.js';
//   <HugeiconsIcon icon={iconForAgentEmoji(a.emoji)} size={15} />

import {
  ChartUpIcon,
  TradeUpIcon,
  TradeDownIcon,
  ChartBarLineIcon,
  NewsIcon,
  RoboticIcon,
  AiBrain01Icon,
  Money01Icon,
  Building06Icon,
  Mail01Icon,
  BubbleChatIcon,
  ComputerIcon,
  SourceCodeIcon,
  Globe02Icon,
  RocketIcon,
  Book02Icon,
  Notification01Icon,
  Search01Icon,
  Calendar03Icon,
  Settings01Icon,
  Note01Icon,
  PaintBoardIcon,
  Target01Icon,
  FlashIcon,
  Fire02Icon,
  PackageIcon,
  ShoppingCart01Icon,
  CustomerSupportIcon,
  Camera01Icon,
  VideoIcon,
  MusicNote01Icon,
  SmartPhone01Icon,
  CloudIcon,
  TestTube01Icon,
  File01Icon,
  Link01Icon,
  Idea01Icon,
  StarIcon,
} from '@hugeicons/core-free-icons';

// Fallback: agentes são bots de IA → ícone de robô (mesmo espírito do <Bot/>
// lucide que era o fallback antigo).
export const AGENT_FALLBACK_ICON = RoboticIcon;

// Chaves SEM variation-selector (U+FE0F) nem ZWJ — a lookup normaliza a entrada.
const MAP = {
  // dados / mercado
  '📈': ChartUpIcon, '💹': TradeUpIcon, '📉': TradeDownIcon, '📊': ChartBarLineIcon,
  // notícias
  '📰': NewsIcon, '🗞': NewsIcon,
  // IA
  '🤖': RoboticIcon, '🧠': AiBrain01Icon,
  // dinheiro
  '💰': Money01Icon, '💵': Money01Icon, '💴': Money01Icon, '💶': Money01Icon, '💷': Money01Icon, '💸': Money01Icon,
  '🏦': Building06Icon,
  // email
  '📧': Mail01Icon, '✉': Mail01Icon, '📨': Mail01Icon, '📩': Mail01Icon,
  // chat
  '💬': BubbleChatIcon, '🗨': BubbleChatIcon,
  // dev
  '💻': ComputerIcon, '🖥': ComputerIcon,
  '👨‍💻': SourceCodeIcon, '👩‍💻': SourceCodeIcon, '🧑‍💻': SourceCodeIcon,
  // web
  '🌐': Globe02Icon, '🌍': Globe02Icon, '🌎': Globe02Icon, '🌏': Globe02Icon,
  // produto / lançamento
  '🚀': RocketIcon,
  // conhecimento
  '📚': Book02Icon, '📖': Book02Icon, '📕': Book02Icon, '📗': Book02Icon, '📘': Book02Icon, '📙': Book02Icon,
  // alertas
  '🔔': Notification01Icon,
  // busca
  '🔍': Search01Icon, '🔎': Search01Icon,
  // agenda
  '📅': Calendar03Icon, '📆': Calendar03Icon, '🗓': Calendar03Icon,
  // config
  '⚙': Settings01Icon, '🛠': Settings01Icon,
  // escrita
  '📝': Note01Icon, '✏': Note01Icon, '✍': Note01Icon,
  // design
  '🎨': PaintBoardIcon,
  // metas
  '🎯': Target01Icon,
  // energia
  '⚡': FlashIcon,
  '🔥': Fire02Icon,
  // logística
  '📦': PackageIcon,
  '🛒': ShoppingCart01Icon, '🛍': ShoppingCart01Icon,
  // suporte
  '🎧': CustomerSupportIcon,
  // mídia
  '📸': Camera01Icon, '📷': Camera01Icon,
  '🎬': VideoIcon, '🎥': VideoIcon, '📹': VideoIcon,
  '🎵': MusicNote01Icon, '🎶': MusicNote01Icon, '🎸': MusicNote01Icon,
  // telefone
  '📞': SmartPhone01Icon, '☎': SmartPhone01Icon, '📱': SmartPhone01Icon,
  // clima
  '☁': CloudIcon, '🌦': CloudIcon, '🌧': CloudIcon, '⛅': CloudIcon,
  // ciência
  '🧪': TestTube01Icon, '🔬': TestTube01Icon,
  // documentos
  '📄': File01Icon, '📃': File01Icon, '📑': File01Icon, '🗂': File01Icon,
  // links
  '🔗': Link01Icon,
  // ideias
  '💡': Idea01Icon,
  // destaque
  '⭐': StarIcon, '🌟': StarIcon, '✨': StarIcon,
};

/**
 * Retorna o IconSvgElement do Huge Icons para o emoji de um agente/registro.
 * Normaliza variation-selectors; casa o emoji inteiro e, em fallback, o 1º
 * code point base. Sempre devolve um ícone (nunca null) — fallback = robô.
 */
export function iconForAgentEmoji(emoji) {
  if (!emoji) return AGENT_FALLBACK_ICON;
  const key = String(emoji).replace(/️/g, '');
  if (MAP[key]) return MAP[key];
  // tenta sem ZWJ (ex.: sequências) e o 1º grapheme base
  const noZwj = key.replace(/‍/g, '');
  if (MAP[noZwj]) return MAP[noZwj];
  const first = [...key][0];
  return MAP[first] || AGENT_FALLBACK_ICON;
}
